// Legal policy content — sourced verbatim from TMP_Policies.docx (Last Updated: August 2026)

export const POLICIES = [
  {
    id: "booking",
    title: "Booking, Cancellation & Transfer Policy",
    intro:
      "By booking a stall or participating in an event organised by The Market Place, the participating business agrees to the following terms.",
    sections: [
      {
        heading: "Booking Confirmation",
        paragraphs: [
          "A stall booking is considered confirmed only after the required payment has been received and confirmation has been issued by The Market Place. Submission of an application or enquiry does not, by itself, guarantee participation or allocation of a particular stall.",
        ],
      },
      {
        heading: "No Cancellations or Refunds",
        paragraphs: [
          "All confirmed stall bookings are non-cancellable and non-refundable. Once payment has been made, no refund, whether full or partial, will be issued if a participating business is unable or chooses not to attend the booked market. This applies irrespective of when the cancellation request is made.",
        ],
      },
      {
        heading: "No Transfers",
        paragraphs: ["Bookings are made specifically for the business and market edition for which they are confirmed. Bookings cannot be:"],
        list: [
          "Transferred to another business, individual or brand",
          "Resold or assigned to a third party",
          "Shifted to another market date at the participating business's discretion",
        ],
      },
      {
        heading: "Exceptional Circumstances",
        paragraphs: [
          "In cases involving a genuine and unavoidable circumstance, a participating business may submit a written request to The Market Place for consideration. Any accommodation in such cases is entirely at the discretion of The Market Place management. Where an exception is approved, the amount paid may be adjusted against participation in a future market edition selected by The Market Place. The participating business will not be entitled to select or demand a particular future date, venue or edition. Such accommodation:",
        ],
        list: [
          "Does not constitute a refund",
          "Is not guaranteed",
          "May be granted only once against the relevant booking",
          "Remains subject to availability, category suitability and approval",
          "Cannot subsequently be transferred or encashed",
        ],
        outro: "The decision of The Market Place management shall be final.",
      },
      {
        heading: "Event Changes",
        paragraphs: [
          "The Market Place may make reasonable changes to market timings, layout, stall allocation, operational arrangements or other event details where required. If an event is postponed or materially altered due to circumstances outside the reasonable control of The Market Place, participating businesses will be informed of the applicable arrangements separately. Unless specifically communicated otherwise by The Market Place, such circumstances do not automatically create an entitlement to a cash refund.",
        ],
      },
    ],
  },
  {
    id: "participation",
    title: "Stall & Vendor Participation Policy",
    intro:
      "Participation in The Market Place is subject to the following operational guidelines. These guidelines are intended to ensure a consistent, safe and well-managed market environment for all participating businesses.",
    sections: [
      {
        heading: "Stall Allocation",
        paragraphs: [
          "All stall locations and numbers are allocated by The Market Place. Where stall selection is offered during booking, final allocation remains subject to operational requirements and confirmation by management. The Market Place reserves the right to modify stall allocations or layouts where reasonably required for event operations.",
        ],
      },
      {
        heading: "Stall Usage",
        paragraphs: [
          "A stall may only be used by the business approved during the booking process. Sharing, subletting, exchanging or transferring a stall to another business is not permitted without prior written approval from The Market Place. Only products and categories disclosed during application and approved by The Market Place may be displayed or sold.",
        ],
      },
      {
        heading: "Tables",
        paragraphs: [
          "Participating businesses may not bring their own tables unless expressly authorised by The Market Place. The standard infrastructure included with a stall, if any, will be specified for each market edition. An additional table may be booked as an add-on for ₹2,400 per additional table. Additional tables are subject to availability and must be requested and paid for in advance.",
        ],
      },
      {
        heading: "Clothing Stands",
        paragraphs: [
          "Clothing stands/racks are not provided by The Market Place. Businesses requiring clothing stands may bring their own, subject to the allocated stall area and event guidelines. Stands must remain within the designated stall space and must not obstruct neighbouring stalls, visitor movement, emergency access or common areas.",
        ],
      },
      {
        heading: "Electrical Connection",
        paragraphs: [
          "Electrical Connection is available as a paid add-on where supported by the venue, at ₹500. Electrical Connection requirements must be disclosed in advance. Availability may depend on the venue and stall location. Participating businesses should not connect high-load equipment or additional electrical equipment without prior approval.",
        ],
      },
      {
        heading: "Stall Boundaries",
        paragraphs: [
          "All merchandise, displays, racks, signage and other material must remain within the allocated stall area. Businesses may not extend displays into walkways or neighbouring stalls without prior approval.",
        ],
      },
      {
        heading: "Setup & Breakdown",
        paragraphs: [
          "Participating businesses must follow the setup, reporting and breakdown timings communicated for each market. Stalls should be fully ready before the market opens to visitors. Early dismantling or packing while the market remains open should be avoided unless specifically permitted by the management team.",
        ],
      },
      {
        heading: "Conduct",
        paragraphs: [
          "Participating businesses are expected to maintain professional and respectful conduct towards visitors, other businesses, venue personnel and The Market Place team. The Market Place reserves the right to intervene where behaviour, displays, products or activities materially affect visitor safety, another participant or the overall market environment.",
        ],
      },
    ],
  },
  {
    id: "terms",
    title: "Terms & Conditions",
    intro:
      "These Terms & Conditions govern participation in markets, use of the website and interactions with The Market Place. By using this website, submitting an application or completing a stall booking, you agree to these Terms & Conditions and the applicable policies published by The Market Place.",
    sections: [
      {
        heading: "Applications & Participation",
        paragraphs: [
          "The Market Place welcomes applications from homegrown businesses, makers, entrepreneurs and independent brands. Submitting an application does not guarantee acceptance. Participation may be evaluated based on factors including:",
        ],
        list: ["Business category", "Product offering", "Available capacity", "Category representation", "Suitability for the relevant edition", "Operational requirements"],
        outro: "The Market Place reserves the right to accept, waitlist or decline an application.",
      },
      {
        heading: "Accuracy of Information",
        paragraphs: ["Applicants are responsible for ensuring that all information submitted during registration is complete and accurate. This includes:"],
        list: ["Brand name", "Contact information", "Instagram handle", "Business category", "Product information", "Electrical Connection requirements", "Other requested operational information"],
        outro: "Any material change should be communicated to The Market Place before the event.",
      },
      {
        heading: "Payments",
        paragraphs: [
          "All applicable stall fees and add-on charges must be paid within the timeframe communicated during booking. A booking is not considered confirmed until payment and confirmation requirements have been completed. Applicable taxes, payment gateway charges or other statutory charges, where relevant, may be charged separately.",
        ],
      },
      {
        heading: "Add-ons",
        paragraphs: ["Optional services may be offered for individual market editions. Current standard add-ons include:"],
        list: ["Electrical Connection: ₹500", "Additional Table: ₹2,400"],
        outro: "Availability must be confirmed for the relevant edition.",
      },
      {
        heading: "Cancellation & Transfers",
        paragraphs: [
          "Confirmed bookings are non-cancellable and non-refundable. Bookings cannot ordinarily be transferred between businesses or market dates. Requests arising from genuine exceptional circumstances may be considered solely at the discretion of The Market Place management in accordance with the Booking, Cancellation & Transfer Policy.",
        ],
      },
      {
        heading: "Products & Compliance",
        paragraphs: [
          "Participating businesses are responsible for ensuring that products displayed or sold comply with applicable laws, licences, registrations, labelling requirements, safety standards and other regulatory obligations relevant to their category. The Market Place provides a marketplace platform and does not assume responsibility for the legality, quality, safety or regulatory compliance of products offered by independent participating businesses.",
        ],
      },
      {
        heading: "Pricing & Transactions",
        paragraphs: ["Each participating business is responsible for its own:"],
        list: ["Product pricing", "Billing", "Payment collection", "Taxes", "Customer service", "Product warranties or guarantees", "Returns or exchanges", "After-sales obligations"],
        outro: "Unless expressly stated otherwise, purchases made from participating businesses are transactions directly between the customer and that business.",
      },
      {
        heading: "Intellectual Property",
        paragraphs: [
          "Participating businesses are responsible for ensuring that their business names, logos, products, photographs, displays and marketing material do not unlawfully infringe third-party intellectual property rights.",
        ],
      },
      {
        heading: "Photography & Event Content",
        paragraphs: [
          "Markets may be photographed or filmed for documentation, publicity, social media, advertising and promotional purposes. By participating in a market, businesses acknowledge that their stalls, products, signage and representatives may incidentally appear in such event content. Where appropriate, The Market Place may also feature participating businesses in market-related promotional communication.",
        ],
      },
      {
        heading: "Safety & Property",
        paragraphs: [
          "Participating businesses remain responsible for their own merchandise, equipment, cash, valuables and personal belongings. The Market Place should not be treated as a custodian of merchandise left at a stall. Participants must comply with venue safety requirements and instructions issued by authorised event personnel.",
        ],
      },
      {
        heading: "Right to Refuse or Discontinue Participation",
        paragraphs: [
          "The Market Place reserves the right to refuse or discontinue participation where there is a serious violation of event policies, safety requirements, applicable law, misrepresentation during application, unauthorised products, or conduct materially detrimental to the market or its participants.",
        ],
      },
      {
        heading: "Limitation of Responsibility",
        paragraphs: ["While The Market Place works to create meaningful commercial and discovery opportunities, participation does not guarantee a particular level of:"],
        list: ["Sales", "Footfall at an individual stall", "Leads", "Revenue", "Social media exposure", "Business growth"],
        outro: "Market performance can vary according to multiple factors outside the control of The Market Place.",
      },
      {
        heading: "Policy Acceptance",
        paragraphs: [
          "Completion of a booking constitutes acknowledgement and acceptance of these Terms & Conditions along with the applicable Booking, Cancellation & Transfer Policy and Stall & Vendor Participation Policy.",
        ],
      },
    ],
  },
  {
    id: "privacy",
    title: "Privacy Policy",
    intro:
      "The Market Place respects the privacy of participating businesses, visitors, partners and website users. This Privacy Policy explains the types of information we may collect, why we collect it and how it may be used.",
    sections: [
      {
        heading: "Information We Collect",
        paragraphs: ["Depending on how you interact with The Market Place, we may collect information including:"],
        list: [
          "Name", "Business/brand name", "Phone number", "Email address", "Instagram or social media handle",
          "Business category", "Product information", "Stall preferences", "Payment and booking status",
          "Previous participation details", "Enquiry information", "Feedback", "Website usage information",
        ],
        outro: "For participating businesses, we may additionally collect information required to manage stall allocation, event communication and operational requirements.",
      },
      {
        heading: "Why We Collect Information",
        paragraphs: ["Information may be used to:"],
        list: [
          "Process stall applications and bookings", "Review participating businesses", "Allocate stalls", "Confirm payments",
          "Communicate market information", "Send setup and operational instructions", "Respond to enquiries",
          "Manage waitlists", "Communicate future market opportunities", "Manage partnerships and collaborations",
          "Improve future markets and services", "Maintain internal business records", "Analyse website and market engagement",
        ],
      },
      {
        heading: "Public Business Information",
        paragraphs: ["Certain information provided by confirmed participating businesses may be displayed publicly in connection with a market edition. This may include:"],
        list: ["Brand name", "Instagram handle", "Stall number", "Business category", "Product highlights"],
        outro:
          "This information helps visitors discover participating businesses and locate them during the market. Private contact numbers or email addresses will not ordinarily be published unless the business has consented or publication is necessary for a specific agreed purpose.",
      },
      {
        heading: "Payment Information",
        paragraphs: [
          "Payments may be processed through third-party payment gateways or financial service providers. The Market Place may receive transaction details necessary to confirm a booking but does not necessarily receive or store complete card, banking or other sensitive payment credentials handled directly by the payment provider.",
        ],
      },
      {
        heading: "Communications",
        paragraphs: [
          "Contact information provided during an application, booking or enquiry may be used for relevant operational and service communications. Where permitted, The Market Place may also communicate information about upcoming markets, participation opportunities and related initiatives. Users may request to stop receiving non-essential promotional communication.",
        ],
      },
      {
        heading: "Sharing of Information",
        paragraphs: ["Personal information is not sold as a commercial database. Information may be shared where reasonably necessary with:"],
        list: [
          "Authorised members of The Market Place team", "Technology and website providers", "Payment service providers",
          "Communication service providers", "Venues or operational partners where necessary", "Professional advisers",
          "Government or regulatory authorities where legally required",
        ],
        outro: "Only information reasonably necessary for the relevant purpose should be shared.",
      },
      {
        heading: "Data Security",
        paragraphs: [
          "Reasonable administrative and technical measures are used to protect information from unauthorised access, misuse, alteration or disclosure. No internet-based system can, however, guarantee absolute security.",
        ],
      },
      {
        heading: "Data Retention",
        paragraphs: [
          "Information may be retained for as long as reasonably necessary for booking records, operational requirements, accounting, legal compliance, dispute resolution and legitimate business purposes.",
        ],
      },
      {
        heading: "Cookies & Analytics",
        paragraphs: [
          "The website may use cookies and analytics technologies to understand website traffic, visitor behaviour and site performance. Third-party services used for analytics or embedded functionality may process information according to their own privacy policies.",
        ],
      },
      {
        heading: "External Links",
        paragraphs: [
          "The website may contain links to Instagram, participating businesses, payment providers, venues or other third-party websites. The Market Place is not responsible for the privacy practices or content of independent third-party platforms.",
        ],
      },
      {
        heading: "Your Information",
        paragraphs: [
          "Users may contact The Market Place to request correction or updating of personal information held about them, subject to applicable legal and operational requirements.",
        ],
      },
      {
        heading: "Policy Updates",
        paragraphs: [
          "This Privacy Policy may be updated periodically to reflect changes in the website, services, technology or applicable requirements. The latest version published on the website will apply.",
        ],
      },
      {
        heading: "Contact",
        paragraphs: ["For privacy-related queries, write to us at themarketplacejaipur@gmail.com, The Market Place, Jaipur, Rajasthan."],
      },
    ],
  },
];
